// Editor Toolbar Component - Premium floating bar with sidebar toggle
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { 
  Plus, Settings, ExternalLink, Globe, GlobeLock, 
  PanelLeftClose, PanelLeft, Sparkles, X, Zap, Inbox
} from 'lucide-react';
import { CARD_TYPES } from './editorConstants';

// All prebuilt cards
const ALL_PREBUILT_CARDS = [
  'ANNOUNCEMENT', 'HIGHLIGHTS', 'HOW_I_INVEST', 'INVESTMENT_FOCUS', 
  'INVESTMENT_THESIS', 'POST_INVESTMENT_SUPPORT', 'STATS_GRID',
  'JOURNEY_TIMELINE', 'ADVICE', 'QUOTE', 'CONTENT_LIST', 'OUTSIDE_OF_WORK', 'LINKS'
];

// Featured cards
const QUICK_ADD_CARDS = ['EMPTY', 'CAROUSEL'];

// Card info for Empty and Carousel
const QUICK_CARD_INFO = {
  EMPTY: { title: 'Empty Card', description: 'Text, tags & links' },
  CAROUSEL: { title: 'Carousel', description: 'Scrolling showcase' }
};

// Get card info helper
const getCardInfo = (type) => CARD_TYPES.find(c => c.type === type);

export default function EditorToolbar({ 
  isAdminMode, 
  isPublished, 
  onAddCard, 
  onEnrich, 
  onSettings, 
  onPreview, 
  onPublish,
  existingCardTypes = [],
  onOpenApps,
  hasApps = false,
  onOpenMailbox,
  newSubmissionsCount = 0
}) {
  const [sidebarMode, setSidebarMode] = useState(() => {
    const saved = localStorage.getItem('editor_sidebar_mode');
    return saved === 'true';
  });
  const [showCardPanel, setShowCardPanel] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    localStorage.setItem('editor_sidebar_mode', sidebarMode);
  }, [sidebarMode]);

  const isCardAdded = (type) => {
    const multipleAllowed = ['QUOTE', 'LINKS', 'EMPTY', 'CAROUSEL'];
    if (multipleAllowed.includes(type)) return false;
    return existingCardTypes.includes(type);
  };

  const getAvailableCards = () => ALL_PREBUILT_CARDS.filter(type => !isCardAdded(type));
  const availableCards = getAvailableCards();

  const handleAddCardClick = (type) => {
    onAddCard(type);
    setShowCardPanel(false);
  };

  // Icon button style
  const iconBtnStyle = {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#666',
    transition: 'all 0.15s ease'
  };

  // Floating toolbar (default mode)
  if (!sidebarMode) {
    return (
      <div style={{
        position: 'fixed',
        top: isAdminMode ? '56px' : '20px',
        right: '20px',
        zIndex: 1000,
      }}>
        {/* Toolbar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          padding: '8px 12px',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.06)'
        }}>
          {/* Sidebar toggle - subtle icon */}
          <button 
            onClick={() => setSidebarMode(true)}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9CA3AF',
              transition: 'all 0.15s ease'
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = '#6B7280'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9CA3AF'; }}
            title="Switch to sidebar mode"
          >
            <PanelLeft style={{ width: '16px', height: '16px' }} />
          </button>

          {/* Divider */}
          <div style={{ width: '1px', height: '24px', background: '#E5E7EB' }} />
          
          {/* Content Group - Add & Enrich */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px', background: '#F9FAFB', borderRadius: '10px' }}>
            <button 
              onClick={() => setShowCardPanel(!showCardPanel)} 
              data-testid="add-card-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '8px',
                border: 'none',
                background: showCardPanel ? '#E5E7EB' : 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                color: showCardPanel ? '#1F2937' : '#4B5563',
                transition: 'all 0.15s ease'
              }}
              onMouseOver={e => { if (!showCardPanel) { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = '#1F2937'; }}}
              onMouseOut={e => { if (!showCardPanel) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#4B5563'; }}}
            >
              <Plus style={{ width: '14px', height: '14px' }} />
              Add
            </button>
            
            <button 
              onClick={onEnrich} 
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '8px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                color: '#7C3AED',
                transition: 'all 0.15s ease'
              }}
              onMouseOver={e => { e.currentTarget.style.background = '#EDE9FE'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Sparkles style={{ width: '14px', height: '14px' }} />
              Enrich
            </button>
          </div>

          {/* Divider */}
          <div style={{ width: '1px', height: '24px', background: '#E5E7EB' }} />

          {/* Apps Group */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px', background: hasApps ? '#ECFDF5' : '#F9FAFB', borderRadius: '10px' }}>
            <button 
              onClick={onOpenApps}
              data-testid="apps-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '8px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                color: hasApps ? '#059669' : '#4B5563',
                transition: 'all 0.15s ease'
              }}
              onMouseOver={e => { e.currentTarget.style.background = hasApps ? '#D1FAE5' : '#F3F4F6'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Zap style={{ width: '14px', height: '14px' }} />
              Apps
              {hasApps && (
                <span style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#10B981'
                }} />
              )}
            </button>

            {hasApps && onOpenMailbox && (
              <button 
                onClick={onOpenMailbox}
                data-testid="inbox-button"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#7C3AED',
                  transition: 'all 0.15s ease',
                  position: 'relative'
                }}
                onMouseOver={e => { e.currentTarget.style.background = '#EDE9FE'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <Inbox style={{ width: '14px', height: '14px' }} />
                Inbox
                {newSubmissionsCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    minWidth: '16px',
                    height: '16px',
                    borderRadius: '100px',
                    background: '#EF4444',
                    color: '#FFFFFF',
                    fontSize: '10px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px'
                  }}>
                    {newSubmissionsCount > 99 ? '99+' : newSubmissionsCount}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Settings - subtle icon */}
          <button 
            onClick={onSettings} 
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9CA3AF',
              transition: 'all 0.15s ease'
            }}
            onMouseOver={e => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = '#6B7280'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9CA3AF'; }}
          >
            <Settings style={{ width: '16px', height: '16px' }} />
          </button>

          {/* Divider */}
          <div style={{ width: '1px', height: '24px', background: '#E5E7EB' }} />

          {/* Actions Group - Preview & Publish */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button 
              onClick={onPreview} 
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                background: '#FFFFFF',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151',
                transition: 'all 0.15s ease'
              }}
              onMouseOver={e => { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = '#FFFFFF'; }}
            >
              <ExternalLink style={{ width: '14px', height: '14px' }} />
              Preview
            </button>

            <button 
              onClick={onPublish}
              data-testid="publish-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 14px',
                borderRadius: '8px',
                border: 'none',
                background: isPublished ? '#059669' : '#1F2937',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                color: '#FFFFFF',
                transition: 'all 0.15s ease'
              }}
              onMouseOver={e => { e.currentTarget.style.background = isPublished ? '#047857' : '#111827'; }}
              onMouseOut={e => { e.currentTarget.style.background = isPublished ? '#059669' : '#1F2937'; }}
            >
              {isPublished ? (
                <><Globe style={{ width: '14px', height: '14px' }} /> Live</>
              ) : (
                <><GlobeLock style={{ width: '14px', height: '14px' }} /> Publish</>
              )}
            </button>
          </div>
        </div>

        {/* Card Panel - positioned directly under toolbar */}
        {showCardPanel && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.05)',
            overflow: 'hidden',
            zIndex: 1001
          }}>
            {/* Header */}
            <div style={{
              padding: '14px 18px',
              borderBottom: '1px solid #F1F5F9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(to bottom, #FAFBFC, #FFFFFF)'
            }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A', letterSpacing: '-0.01em' }}>Add Card</span>
              <button
                onClick={() => setShowCardPanel(false)}
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#F1F5F9',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748B',
                  transition: 'all 0.15s ease'
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = '#E2E8F0'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = '#F1F5F9'; }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Featured Cards - Empty & Carousel */}
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #F1F5F9' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                {QUICK_ADD_CARDS.map((type) => {
                  const info = QUICK_CARD_INFO[type];
                  const isCarousel = type === 'CAROUSEL';
                  const isHovered = hoveredCard === type;
                  
                  return (
                    <button
                      key={type}
                      onClick={() => handleAddCardClick(type)}
                      onMouseEnter={() => setHoveredCard(type)}
                      onMouseLeave={() => setHoveredCard(null)}
                      data-testid={`add-${type.toLowerCase()}-card`}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        border: `1px solid ${isCarousel ? (isHovered ? '#C4B5FD' : '#EDE9FE') : (isHovered ? '#CBD5E1' : '#E2E8F0')}`,
                        background: isCarousel 
                          ? (isHovered ? '#FAF5FF' : '#FAFBFF') 
                          : (isHovered ? '#F8FAFC' : '#FFFFFF'),
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'left',
                        transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
                        boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.06)' : 'none'
                      }}
                    >
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: isCarousel ? '#F3E8FF' : '#F1F5F9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {type === 'EMPTY' ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="3" strokeDasharray="4 2"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="1.5">
                            <rect x="2" y="6" width="6" height="12" rx="1"/>
                            <rect x="9" y="6" width="6" height="12" rx="1"/>
                            <rect x="16" y="6" width="6" height="12" rx="1"/>
                          </svg>
                        )}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: isCarousel ? '#7C3AED' : '#1E293B',
                          marginBottom: '1px',
                          letterSpacing: '-0.01em'
                        }}>
                          {info.title}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: isCarousel ? '#A78BFA' : '#94A3B8',
                          letterSpacing: '0'
                        }}>
                          {info.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Special Cards */}
            <div style={{ padding: '12px 14px 8px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '10px'
              }}>
                <span style={{ 
                  fontSize: '10px', 
                  fontWeight: '600', 
                  color: '#8B5CF6', 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px',
                  background: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Sparkles className="w-3 h-3" />
                  Special Cards
                </span>
                <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                  {availableCards.length} available
                </span>
              </div>

              {/* Card grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '8px',
                maxHeight: '220px',
                overflowY: 'auto',
                paddingRight: '4px'
              }}>
                {availableCards.map((type) => {
                  const cardInfo = getCardInfo(type);
                  if (!cardInfo) return null;
                  const isHovered = hoveredCard === type;

                  return (
                    <button
                      key={type}
                      onClick={() => handleAddCardClick(type)}
                      onMouseEnter={() => setHoveredCard(type)}
                      onMouseLeave={() => setHoveredCard(null)}
                      data-testid={`add-${type.toLowerCase().replace(/_/g, '-')}-card`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: 'none',
                        background: isHovered ? '#FFFFFF' : '#F8FAFC',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s ease',
                        boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                        transform: isHovered ? 'translateY(-1px)' : 'translateY(0)'
                      }}
                    >
                      <span style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '8px',
                        background: '#F1F5F9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        flexShrink: 0
                      }}>
                        {cardInfo.icon}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#1E293B',
                          marginBottom: '1px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {cardInfo.label}
                        </div>
                        <div style={{
                          fontSize: '10px',
                          color: '#94A3B8',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {cardInfo.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {availableCards.length === 0 && (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center', 
                  color: '#94A3B8',
                  fontSize: '12px'
                }}>
                  All cards added! 🎉
                </div>
              )}
            </div>
          </div>
        )}

        {/* Click outside to close */}
        {showCardPanel && (
          <div 
            style={{ position: 'fixed', inset: 0, zIndex: -1 }}
            onClick={() => setShowCardPanel(false)}
          />
        )}
      </div>
    );
  }

  // Sidebar mode
  return (
    <div style={{
      position: 'fixed',
      top: isAdminMode ? '48px' : '0',
      left: '0',
      bottom: '0',
      width: showCardPanel ? '340px' : '64px',
      zIndex: 1000,
      display: 'flex',
      transition: 'width 0.2s ease',
    }}>
      {/* Sidebar icons */}
      <div style={{
        width: '64px',
        height: '100%',
        background: 'rgba(255,255,255,0.98)',
        backdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '14px',
        paddingBottom: '14px',
        gap: '4px'
      }}>
        <button
          onClick={() => setSidebarMode(false)}
          style={{ ...iconBtnStyle, width: '40px', height: '40px', marginBottom: '6px' }}
          onMouseOver={(e) => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = '#1E293B'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#666'; }}
          title="Switch to floating toolbar"
        >
          <PanelLeftClose className="w-[18px] h-[18px]" />
        </button>

        <div style={{ width: '28px', height: '1px', background: '#E5E7EB', marginBottom: '6px' }} />

        <button
          onClick={() => setShowCardPanel(!showCardPanel)}
          style={{
            ...iconBtnStyle,
            width: '40px',
            height: '40px',
            background: showCardPanel ? '#F1F5F9' : 'transparent',
            color: showCardPanel ? '#1E293B' : '#666'
          }}
          onMouseOver={(e) => { if (!showCardPanel) { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = '#1E293B'; }}}
          onMouseOut={(e) => { if (!showCardPanel) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#666'; }}}
          title="Add Card"
        >
          <Plus className="w-[18px] h-[18px]" />
        </button>

        <button
          onClick={onEnrich}
          style={{ ...iconBtnStyle, width: '40px', height: '40px', color: '#8B5CF6' }}
          onMouseOver={(e) => { e.currentTarget.style.background = '#F5F3FF'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
          title="Enrich with AI"
        >
          <Sparkles className="w-[18px] h-[18px]" />
        </button>

        <button
          onClick={onOpenApps}
          style={{ 
            ...iconBtnStyle, 
            width: '40px', 
            height: '40px', 
            color: hasApps ? '#10B981' : '#666',
            position: 'relative'
          }}
          onMouseOver={(e) => { e.currentTarget.style.background = hasApps ? '#ECFDF5' : '#F1F5F9'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
          title="Apps"
        >
          <Zap className="w-[18px] h-[18px]" />
          {hasApps && (
            <span style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#10B981'
            }} />
          )}
        </button>

        {hasApps && onOpenMailbox && (
          <button
            onClick={onOpenMailbox}
            style={{ 
              ...iconBtnStyle, 
              width: '40px', 
              height: '40px', 
              color: '#8B5CF6',
              position: 'relative'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = '#F5F3FF'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
            title="Inbox"
          >
            <Inbox className="w-[18px] h-[18px]" />
            {newSubmissionsCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                minWidth: '14px',
                height: '14px',
                borderRadius: '100px',
                background: '#EF4444',
                color: '#FFFFFF',
                fontSize: '9px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 3px'
              }}>
                {newSubmissionsCount > 99 ? '99+' : newSubmissionsCount}
              </span>
            )}
          </button>
        )}

        <button
          onClick={onSettings}
          style={{ ...iconBtnStyle, width: '40px', height: '40px' }}
          onMouseOver={(e) => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = '#1E293B'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#666'; }}
          title="Settings"
        >
          <Settings className="w-[18px] h-[18px]" />
        </button>

        <div style={{ width: '28px', height: '1px', background: '#E5E7EB', margin: '6px 0' }} />

        <button
          onClick={onPreview}
          style={{ ...iconBtnStyle, width: '40px', height: '40px' }}
          onMouseOver={(e) => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = '#1E293B'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#666'; }}
          title="Preview"
        >
          <ExternalLink className="w-[18px] h-[18px]" />
        </button>

        <button
          onClick={onPublish}
          style={{
            ...iconBtnStyle,
            width: '40px',
            height: '40px',
            background: isPublished ? '#10B981' : '#1E293B',
            color: '#fff',
            boxShadow: isPublished ? '0 2px 8px rgba(16, 185, 129, 0.25)' : '0 2px 8px rgba(0,0,0,0.12)'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          title={isPublished ? 'Published' : 'Publish'}
        >
          {isPublished ? <Globe className="w-[18px] h-[18px]" /> : <GlobeLock className="w-[18px] h-[18px]" />}
        </button>

        <div style={{ flex: 1 }} />
      </div>

      {/* Sidebar card panel */}
      {showCardPanel && (
        <div style={{
          width: '276px',
          height: '100%',
          background: 'rgba(250,251,252,0.98)',
          backdropFilter: 'blur(20px)',
          borderRight: '1px solid rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 16px',
            borderBottom: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(to bottom, #FAFBFC, transparent)'
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A', letterSpacing: '-0.01em' }}>Add Card</span>
            <button
              onClick={() => setShowCardPanel(false)}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                border: 'none',
                background: '#F1F5F9',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748B'
              }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Featured Cards - horizontal slim layout */}
          <div style={{ padding: '12px', borderBottom: '1px solid #F1F5F9' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {QUICK_ADD_CARDS.map((type) => {
                const info = QUICK_CARD_INFO[type];
                const isCarousel = type === 'CAROUSEL';
                const isHovered = hoveredCard === type;
                
                return (
                  <button
                    key={type}
                    onClick={() => handleAddCardClick(type)}
                    onMouseEnter={() => setHoveredCard(type)}
                    onMouseLeave={() => setHoveredCard(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: `1px solid ${isCarousel ? (isHovered ? '#C4B5FD' : '#EDE9FE') : (isHovered ? '#CBD5E1' : '#E2E8F0')}`,
                      background: isCarousel 
                        ? (isHovered ? '#FAF5FF' : '#FAFBFF') 
                        : (isHovered ? '#F8FAFC' : '#FFFFFF'),
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'left',
                      transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
                      boxShadow: isHovered ? '0 3px 10px rgba(0,0,0,0.05)' : 'none'
                    }}
                  >
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: isCarousel ? '#F3E8FF' : '#F1F5F9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {type === 'EMPTY' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="3" strokeDasharray="4 2"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="1.5">
                          <rect x="2" y="6" width="6" height="12" rx="1"/>
                          <rect x="9" y="6" width="6" height="12" rx="1"/>
                          <rect x="16" y="6" width="6" height="12" rx="1"/>
                        </svg>
                      )}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: isCarousel ? '#7C3AED' : '#1E293B',
                        marginBottom: '1px'
                      }}>
                        {info.title}
                      </div>
                      <div style={{
                        fontSize: '10px',
                        color: isCarousel ? '#A78BFA' : '#94A3B8'
                      }}>
                        {info.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Special Cards */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ 
              padding: '10px 12px 6px',
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px'
            }}>
              <span style={{ 
                fontSize: '9px', 
                fontWeight: '600', 
                color: '#8B5CF6', 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px',
                background: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)',
                padding: '3px 8px',
                borderRadius: '5px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px'
              }}>
                <Sparkles className="w-2.5 h-2.5" />
                Special Cards
              </span>
              <span style={{ fontSize: '10px', color: '#94A3B8' }}>
                {availableCards.length} available
              </span>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '6px 10px 10px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {availableCards.map((type) => {
                  const cardInfo = getCardInfo(type);
                  if (!cardInfo) return null;
                  const isHovered = hoveredCard === type;

                  return (
                    <button
                      key={type}
                      onClick={() => handleAddCardClick(type)}
                      onMouseEnter={() => setHoveredCard(type)}
                      onMouseLeave={() => setHoveredCard(null)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        border: 'none',
                        background: isHovered ? '#FFFFFF' : '#F8FAFC',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s ease',
                        boxShadow: isHovered ? '0 2px 6px rgba(0,0,0,0.05)' : 'none',
                        transform: isHovered ? 'translateY(-1px)' : 'translateY(0)'
                      }}
                    >
                      <span style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '6px',
                        background: '#F1F5F9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        flexShrink: 0
                      }}>
                        {cardInfo.icon}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '500',
                          color: '#1E293B'
                        }}>
                          {cardInfo.label}
                        </div>
                        <div style={{
                          fontSize: '9px',
                          color: '#94A3B8'
                        }}>
                          {cardInfo.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {availableCards.length === 0 && (
                <div style={{ 
                  padding: '16px', 
                  textAlign: 'center', 
                  color: '#94A3B8',
                  fontSize: '11px'
                }}>
                  All cards added! 🎉
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
